import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays, MapPin, Ticket } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getAdminSaleEvents, deleteSaleEvent, type AdminSaleEvent, getAdminSettings, updateAdminSetting, uploadAsset } from '../lib/api'
import RowActions from '../components/RowActions'
import { useConfirm } from '../components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

function EventCard({
  event,
  onNavigate,
  onEdit,
  onDelete,
}: {
  event: AdminSaleEvent
  onNavigate: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div
      className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden cursor-pointer hover:border-zinc-700 transition-colors group"
      onClick={onNavigate}
    >
      {/* Banner / Placeholder */}
      <div className="h-36 bg-zinc-800 relative overflow-hidden">
        {event.banner_image_url ? (
          <img
            src={normalizeImageKitUrl(event.banner_image_url)}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <Ticket size={32} className="text-zinc-700" />
          </div>
        )}

        {/* Status badge overlay */}
        <div className="absolute top-3 right-3">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded backdrop-blur-sm ${
            event.is_published
              ? 'bg-green-900/80 text-green-400'
              : 'bg-zinc-900/80 text-zinc-400'
          }`}>
            {event.is_published ? 'Published' : 'Draft'}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="p-4">
        <h2 className="text-sm font-semibold text-white mb-2 line-clamp-1">{event.title}</h2>

        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <CalendarDays size={12} className="shrink-0" />
            <span>{[event.date, event.time].filter(Boolean).join(' · ')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <MapPin size={12} className="shrink-0" />
            <span className="line-clamp-1">{event.location}</span>
          </div>
        </div>
      </div>

      {/* Card footer */}
      <div
        className="px-4 pb-4 flex items-center justify-between"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-[10px] text-zinc-600">
          {new Date(event.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
        <RowActions
          actions={[
            { label: 'Edit', onClick: onEdit },
            { label: 'Hapus', onClick: onDelete, variant: 'danger' },
          ]}
        />
      </div>
    </div>
  )
}

export default function SaleEventsPage() {
  const { token } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'events' | 'settings'>('events')

  // Events state
  const [events, setEvents] = useState<AdminSaleEvent[]>([])
  const [loading, setLoading] = useState(true)
  const { confirm, dialog } = useConfirm()

  // General Settings (QR QRIS) state
  const [qrisUrl, setQrisUrl] = useState('')
  const [preview, setPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const pendingFile = useRef<File | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  async function load() {
    if (!token) return
    setLoading(true)
    try {
      setEvents(await getAdminSaleEvents(token))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [token])

  useEffect(() => {
    if (!token) return
    getAdminSettings(token)
      .then((settings) => {
        const qris = settings.find((s) => s.key === 'qris_url')
        if (qris?.value) setQrisUrl(qris.value)
      })
      .catch(console.error)
  }, [token])

  async function handleDelete(id: string, title: string) {
    if (!token || !await confirm(`"${title}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${title}"` })) return
    await deleteSaleEvent(token, id)
    await load()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    pendingFile.current = file
    setPreview(URL.createObjectURL(file))
  }

  const handleSave = async () => {
    if (!token) return
    setSaving(true)
    setSaved(false)
    try {
      let url = qrisUrl
      if (pendingFile.current) {
        const asset = await uploadAsset(token, pendingFile.current, { filename: 'qris-payment' })
        url = asset.url
        pendingFile.current = null
        setPreview(null)
      }
      await updateAdminSetting(token, 'qris_url', url)
      setQrisUrl(url)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      alert('Gagal menyimpan: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setSaving(false)
    }
  }

  const publishedCount = events.filter(e => e.is_published).length
  const draftCount = events.filter(e => !e.is_published).length

  const labelCls = 'block text-xs text-zinc-400 mb-1'

  return (
    <div className="p-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Sale Events</h1>
        {activeTab === 'events' && (
          <button
            onClick={() => navigate('/sales/sale-events/create')}
            className="rounded bg-yellow-500 px-4 py-2 text-xs font-bold text-black hover:bg-yellow-400 transition-colors"
          >
            + CREATE EVENT
          </button>
        )}
      </div>

      {/* Tab navigation */}
      <div className="flex items-center gap-1 border-b border-zinc-800 -mb-2">
        {(['events', 'settings'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'text-white border-yellow-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            {tab === 'events' ? 'Events' : 'General Settings'}
          </button>
        ))}
      </div>

      {/* Tab: Events */}
      {activeTab === 'events' && (
        <>
          {/* Mini stats */}
          {!loading && events.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="rounded-lg border border-green-800 bg-green-900/20 px-3 py-1.5 text-xs font-semibold text-green-400">
                {publishedCount} Published
              </span>
              <span className="rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-1.5 text-xs font-semibold text-zinc-400">
                {draftCount} Draft
              </span>
              <span className="text-xs text-zinc-600">Total {events.length} event</span>
            </div>
          )}

          {/* Event list */}
          {loading ? (
            <p className="text-zinc-400 text-sm">Loading...</p>
          ) : events.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-12 text-center">
              <Ticket size={32} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">Belum ada sale event.</p>
              <p className="text-zinc-600 text-xs mt-1">Buat event pertama untuk mulai berjualan tiket.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {events.map((e) => (
                <EventCard
                  key={e.id}
                  event={e}
                  onNavigate={() => navigate(`/sales/sale-events/${e.id}/overview`)}
                  onEdit={() => navigate(`/sales/sale-events/${e.id}/edit`)}
                  onDelete={() => handleDelete(e.id, e.title)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Tab: General Settings */}
      {activeTab === 'settings' && (
        <div className="max-w-lg space-y-6">
          <div className="rounded border border-zinc-800 bg-zinc-900/30 p-6 space-y-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Pembayaran — QRIS</h2>

            <div className="space-y-3">
              <label className={labelCls}>QR QRIS</label>

              {(preview ?? qrisUrl) && (
                <div className="inline-block rounded-lg border border-zinc-700 bg-white p-2">
                  <img
                    src={normalizeImageKitUrl(preview ?? qrisUrl)}
                    alt="QR QRIS"
                    className="block max-w-[180px]"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                </div>
              )}

              <div>
                <p className="text-xs text-zinc-500 mb-1">Upload gambar baru</p>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-500 transition-colors"
                >
                  Pilih File
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
                {preview && (
                  <span className="ml-3 text-xs text-zinc-500">{pendingFile.current?.name}</span>
                )}
              </div>

            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded bg-yellow-500 px-6 py-2 text-xs font-bold text-black hover:bg-yellow-400 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
              {saved && <span className="text-xs text-green-400">Tersimpan</span>}
            </div>
          </div>
        </div>
      )}

      {dialog}
    </div>
  )
}
